import React, { useState } from 'react';
import { Event } from '../config/appwrite';
import Player from './player';

interface EventModalProps {
  event: Event | null;
  isOpen: boolean;
  onClose: () => void;
  onUpdateEvent: (eventId: string, updates: Partial<Event>) => void;
  onDeleteEvent: (eventId: string) => void;
}

const EventModal: React.FC<EventModalProps> = ({
  event,
  isOpen,
  onClose,
  onUpdateEvent,
  onDeleteEvent,
}) => {
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  if (!isOpen || !event) return null;

  const handleToggleActive = async () => {
    if (!event.is_active) {
      await onUpdateEvent('all', { is_active: false });
      await onUpdateEvent(event.id, { is_active: true });
    } else {
      await onUpdateEvent(event.id, { is_active: false });
    }
  };

  const confirmDelete = () => {
    onDeleteEvent(event.id);
    setShowDeleteConfirm(false);
    onClose();
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-start justify-center z-50 p-3 sm:p-6 overflow-y-auto">
      <div className="w-full max-w-3xl bg-white rounded-2xl shadow-2xl my-4 overflow-hidden">

        {/* Header */}
        <div className="bg-[#0e4653] px-5 sm:px-7 py-5">
          <div className="flex items-start justify-between gap-4">
            <div className="flex-1 min-w-0">
              <h3 className="text-lg sm:text-xl font-semibold text-white truncate">{event.name}</h3>
              <div className="flex items-center gap-3 mt-1.5">
                <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold ${
                  event.is_active
                    ? 'bg-emerald-500/20 text-emerald-300 border border-emerald-500/30'
                    : 'bg-white/10 text-white/60 border border-white/20'
                }`}>
                  <span className={`w-1.5 h-1.5 rounded-full ${event.is_active ? 'bg-emerald-400' : 'bg-white/40'}`} />
                  {event.is_active ? 'Active' : 'Inactive'}
                </span>
                <span className="text-[#7fb8c8] text-xs">
                  Created {new Date(event.created_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}
                </span>
              </div>
            </div>
            <button
              onClick={onClose}
              className="p-2 rounded-lg text-white/70 hover:text-white hover:bg-white/10 transition-colors flex-shrink-0"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>

        {/* Body */}
        <div className="px-5 sm:px-7 py-6 space-y-5 max-h-[calc(90vh-100px)] overflow-y-auto">

          {/* Video Player */}
          <div className="rounded-xl overflow-hidden bg-black">
            <div className="relative w-full pt-[56.25%]">
              <div className="absolute inset-0">
                <Player videoId={event.youtube_link} />
              </div>
            </div>
          </div>

          {/* Event Details Card */}
          <div className="border border-[#0e4653]/15 rounded-xl overflow-hidden">
            <div className="bg-[#0e4653]/5 px-5 py-3 border-b border-[#0e4653]/10">
              <h4 className="text-sm font-semibold text-[#0e4653]">Event Details</h4>
            </div>
            <div className="p-5 space-y-3">
              <div className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-3">
                <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide sm:w-28 flex-shrink-0">YouTube ID</span>
                <span className="font-mono text-sm text-[#0e4653] bg-[#0e4653]/5 px-2.5 py-1 rounded-lg break-all">
                  {event.youtube_link}
                </span>
              </div>
              <div className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-3">
                <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide sm:w-28 flex-shrink-0">Status</span>
                <span className={`text-sm font-medium ${event.is_active ? 'text-emerald-600' : 'text-gray-500'}`}>
                  {event.is_active ? 'Currently Active' : 'Inactive'}
                </span>
              </div>
              <div className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-3">
                <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide sm:w-28 flex-shrink-0">Event ID</span>
                <span className="font-mono text-xs text-gray-400 break-all">{event.id}</span>
              </div>
            </div>
          </div>

          {/* Action Bar */}
          <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-3 pt-1">
            <div className="flex gap-2">
              <button
                onClick={handleToggleActive}
                className={`flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm font-semibold transition-colors ${
                  event.is_active
                    ? 'bg-orange-50 text-orange-700 hover:bg-orange-100 border border-orange-200'
                    : 'bg-emerald-50 text-emerald-700 hover:bg-emerald-100 border border-emerald-200'
                }`}
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  {event.is_active ? (
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                      d="M10 9v6m4-6v6m7-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                  ) : (
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                      d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  )}
                </svg>
                {event.is_active ? 'Deactivate' : 'Activate'}
              </button>

              <button
                onClick={() => setShowDeleteConfirm(true)}
                className="flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm font-semibold bg-red-50 text-red-600 hover:bg-red-100 border border-red-200 transition-colors"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                    d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                </svg>
                Delete
              </button>
            </div>

            <button
              onClick={onClose}
              className="px-5 py-2.5 border border-gray-300 rounded-lg text-sm font-medium text-gray-600 hover:bg-gray-50 transition-colors"
            >
              Close
            </button>
          </div>
        </div>
      </div>

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-60 p-4">
          <div className="w-full max-w-sm bg-white rounded-2xl shadow-2xl overflow-hidden">
            <div className="p-6 text-center">
              <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg className="w-6 h-6 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                    d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                </svg>
              </div>
              <h3 className="text-base font-semibold text-gray-900 mb-1">Delete Event</h3>
              <p className="text-sm text-gray-500">
                Are you sure you want to delete <span className="font-medium text-gray-700">"{event.name}"</span>? This action cannot be undone.
              </p>
              <div className="flex gap-3 mt-6">
                <button
                  onClick={() => setShowDeleteConfirm(false)}
                  className="flex-1 px-4 py-2.5 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={confirmDelete}
                  className="flex-1 px-4 py-2.5 bg-red-600 text-white rounded-lg text-sm font-semibold hover:bg-red-700 transition-colors"
                >
                  Delete
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default EventModal;
